import type React from 'react';

import { Alert, Badge, Button, Image, Text } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';
import { useKnownAvailableParams } from '@change-corgi/core-react/router';

import endorsementsBannerImage2x from '#src/assets/starterDashboardV2/endorsements/banner-image-2x.png';

import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { useEndorsements } from '#src/app/shared/components/endorsements';
import { useEndorsementsFeature } from '#src/app/shared/hooks/useEndorsementsFeature';

import { useBannerDismissalStorage } from '../PageBanner/hooks/useBannerDismissalStorage';

import { ENDORSEMENTS_BANNER_KEY } from './constants';
import { useEndorsementsBanner } from './hooks/useEndorsementsBanner';

const MINIMUM_SIGNATURES_TO_SHOW_BANNER = 100;
const ENDORSEMENTS_MAX_PER_PETITION = 10;

const EndorsementsBannerContent: React.FC = () => {
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const { translate } = useI18n();
	const { actions } = useEndorsementsBanner();

	const endorsementAddUrl = `/p/${slug}/dashboard/endorsements/add`;

	return (
		<div className="p-4 pt-2.5 pb-0 lg:p-6">
			<Alert
				className="relative h-full w-full overflow-hidden lg:min-h-50 [&_button]:cursor-pointer [&>div>div>:first-child]:hidden"
				containerClassName="[&>div]:flex-row [&>div]:items-center [&>div>div:last-child]:flex [&>div>div:last-child]:flex-row [&>div>div:last-child]:items-center [&>div>div:last-child]:gap-6 [&>div>div:last-child]:w-full"
				variant="info"
				onDismissClick={actions.handleDismiss}
				dismissButtonAriaLabel={translate(
					'corgi.petition.starter_dashboard_v2.endorsements.banner.dismiss_button_aria_label',
				)}
				data-testid="endorsements-banner"
				data-qa="endorsements-banner"
			>
				<div className="flex w-full flex-col gap-8 pb-50 lg:flex-row lg:items-center lg:gap-6 lg:pb-0">
					<div className="flex flex-1 flex-col lg:pr-[290px] xl:max-w-xl xl:pr-0">
						<div className="flex flex-col gap-0.5">
							<div className="flex flex-col-reverse gap-0.5 lg:flex-row lg:items-center lg:gap-0.5">
								<Text variant="body" size="large" fontWeight="bold">
									<Translate value="corgi.petition.starter_dashboard_v2.endorsements.banner.heading" />
								</Text>
								<Badge
									label={<Translate value="corgi.petition.starter_dashboard_v2.endorsements.banner.badge" />}
									size="small"
									variant="information"
									emphasis="strong"
									data-testid="endorsements-banner-badge"
									data-qa="endorsements-banner-badge"
									className="mb-4 h-6 w-fit min-w-6 shrink-0 px-1.5 py-0 text-sm leading-5 font-bold tracking-normal text-white lg:mb-0 lg:ml-2"
								/>
							</div>
							<Text as="p">
								<Translate value="corgi.petition.starter_dashboard_v2.endorsements.banner.body" />
							</Text>
						</div>
						<Button
							as="a"
							className="mt-5 w-full text-center lg:w-fit"
							variant="secondary-solid"
							size="x-small"
							href={endorsementAddUrl}
							rel="noopener noreferrer"
							onClick={actions.handleCtaClick}
							data-testid="add-endorsement-button"
							data-qa="add-endorsement-button"
						>
							<Translate value="corgi.petition.starter_dashboard_v2.endorsements.banner.add_button" />
						</Button>
					</div>
					<div className="absolute -bottom-1 left-1/2 -translate-x-1/2 lg:top-6 lg:right-12 lg:left-auto lg:translate-x-0">
						<Image
							src={endorsementsBannerImage2x}
							alt="Endorsements banner illustration"
							className="h-full min-h-45 w-64 min-w-64"
							data-testid="endorsements-banner-image"
							data-qa="endorsements-banner-image"
						/>
					</div>
				</div>
			</Alert>
		</div>
	);
};

export const EndorsementsBanner: React.FC = () => {
	const {
		data: { petition, viewer },
	} = useStarterDashboardV2PageData();
	const { isEnabled: isEndorsementsEnabled } = useEndorsementsFeature(viewer.experiments);
	const { isBannerDismissed } = useBannerDismissalStorage();
	const {
		data: { totalCount },
	} = useEndorsements({ petitionSlugOrId: petition.slug });

	const signatureCount = petition.signatureState.signatureCount.displayed;
	const hasMinimumSignatures = signatureCount > MINIMUM_SIGNATURES_TO_SHOW_BANNER;
	const isDismissed = isBannerDismissed(ENDORSEMENTS_BANNER_KEY);
	const isAtEndorsementLimit = totalCount >= ENDORSEMENTS_MAX_PER_PETITION;

	if (!isEndorsementsEnabled || isDismissed || !hasMinimumSignatures || isAtEndorsementLimit) {
		return null;
	}

	return <EndorsementsBannerContent />;
};
